import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import TextEditor from 'flarum/common/components/TextEditor';

/**
 * A TextEditor without the built-in "submit" control — the record editor has
 * its own publish button. By subclassing core's TextEditor we inherit every
 * prototype patch other extensions apply (fof/upload's image button + drag-drop,
 * mentions, emoji, fof/rich-text's WYSIWYG), so the body field behaves exactly
 * like Flarum's native composer.
 */
/** The attrs MarkdownEditor hands it; core declares TextEditor's loosely. */

class RecordTextEditor extends TextEditor {
  pbUploading = false;
  controlItems() {
    const items = super.controlItems();
    if (items.has('submit')) items.remove('submit');

    // fof/upload patches the composer, not necessarily this editor's toolbar,
    // so surface our own attach control. Only when uploads are actually
    // available — otherwise there is nothing behind the button.
    if (app.forum.attribute('fof-upload.canUpload') !== undefined) {
      items.add('pb-upload', m("button", {
        type: "button",
        className: "Button Button--icon Button--link PB-MarkdownEditor-upload",
        title: String(app.translator.trans('ernestdefoe-page-builder.forum.image.upload_hint') ?? 'Upload an image'),
        disabled: this.pbUploading,
        onclick: () => this.pbPickFile()
      }, m("i", {
        className: `fas ${this.pbUploading ? 'fa-spinner fa-spin' : 'fa-paperclip'}`
      })), 50);
    }
    return items;
  }

  /** Open a file picker and insert whatever comes back at the cursor. */
  pbPickFile() {
    if (this.pbUploading) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => {
      const file = input.files && input.files[0];
      if (file) this.pbUpload(file);
    };
    input.click();
  }
  pbUpload(file) {
    this.pbUploading = true;
    m.redraw();
    const body = new FormData();
    body.append('files[]', file);
    app.request({
      method: 'POST',
      url: `${app.forum.attribute('apiUrl')}/fof/upload`,
      body,
      serialize: raw => raw
    }).then(res => {
      const f = res?.data?.[0]?.attributes;
      const url = f?.url;
      if (url) {
        const md = `\n![${f?.baseName || file.name}](${url})\n`;
        // Insert at the caret when we have the driver, else append.
        const attrs = this.attrs;
        const editor = attrs?.composer?.editor;
        if (editor?.insertAtCursor) editor.insertAtCursor(md);else if (attrs.onchange) attrs.onchange((attrs.value || '') + md);
      }
      this.pbUploading = false;
      m.redraw();
    }).catch(() => {
      this.pbUploading = false;
      app.alerts.show({
        type: 'error'
      }, app.translator.trans('ernestdefoe-page-builder.forum.image.upload_error'));
      m.redraw();
    });
  }

  // The editor owns its value; never re-submit on Enter.
  onsubmit() {
    /* no-op */
  }
}

/**
 * Field input for the `markdown` type. Hosts a persistent composer object that
 * the embedded editor writes its driver onto, and forwards edits to onChange.
 */
export default class MarkdownEditor extends Component {
  // Stable object the TextEditor assigns `.editor` (its driver) onto; fof/upload
  // reads composer.editor to insert uploaded images at the cursor.
  composer = {
    editor: null
  };
  view() {
    const a = this.attrs;
    return m("div", {
      className: "PB-MarkdownEditor"
    }, m(RecordTextEditor, {
      composer: this.composer,
      value: a.value ?? '',
      disabled: false,
      submitLabel: '',
      placeholder: a.field.placeholder || app.translator.trans('ernestdefoe-page-builder.forum.markdown_placeholder'),
      onchange: val => a.onChange(val),
      onsubmit: () => {}
    }));
  }
}
flarum.reg.add('ernestdefoe-page-builder', 'forum/components/MarkdownEditor', MarkdownEditor);